'use client';

import Link from 'next/link';
import { useState, useEffect } from 'react';
import Image from 'next/image';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import { productManagementService } from '@/services/productManagementService';
import { Product } from '@/types/database.types';
import { useAuth } from '@/contexts/AuthContext';
import { AuthAwareProductCard } from '@/components/products/ProductCard';

const MechanicalPartsPage = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [priceRange, setPriceRange] = useState<'all' | 'under100' | '100-500' | '500-1000' | 'over1000'>('all');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user, loading: authLoading } = useAuth();

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setProductsLoading(true);
      setError(null);
      const { data, error: fetchError } = await productManagementService.getByCategory('Structural');
      
      if (fetchError) {
        setError('Unable to load products. Please try again later.');
        return;
      }
      
      setProducts(data || []);
    } catch (err) {
      setError('An unexpected error occurred while loading products.');
    } finally {
      setProductsLoading(false);
    }
  };

  const filteredProducts = products.filter((product) => {
    const matchesSearch = 
      product.part_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description?.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesPrice = true;
    if (!authLoading && user) {
      const price = product.price || 0;
      switch (priceRange) {
        case 'under100':
          matchesPrice = price < 100;
          break;
        case '100-500':
          matchesPrice = price >= 100 && price < 500;
          break;
        case '500-1000':
          matchesPrice = price >= 500 && price < 1000;
          break;
        case 'over1000':
          matchesPrice = price >= 1000;
          break;
      }
    }

    return matchesSearch && matchesPrice;
  });

  const openProductModal = (product: Product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
  };

  const closeProductModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
    // Restore body scroll
    document.body.style.overflow = 'unset';
  };

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isModalOpen) {
        closeProductModal();
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isModalOpen]);

  return (
    <>
      <Header />
      <div className="min-h-screen bg-white">
        {/* Hero Section — black/red color blocking */}
        <section className="relative bg-black py-16 lg:py-24 overflow-hidden">
          {/* Red geometric accent — left border */}
          <div className="absolute left-0 top-0 bottom-0 w-2 bg-red-600" />
          {/* Red geometric accent — corner triangle */}
          <div className="absolute top-0 right-0 w-48 h-48 bg-red-600 opacity-10" style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }} />

          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="max-w-3xl">
              <Link
                href="/products"
                className="inline-flex items-center text-red-400 hover:text-red-300 transition-colors duration-fast mb-6 group"
              >
                <svg
                  className="w-5 h-5 mr-2 transition-transform duration-medium group-hover:-translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                <span className="text-sm font-semibold uppercase tracking-wide">Back to Product Categories</span>
              </Link>
              <div className="inline-flex items-center gap-2 mb-4">
                <div className="w-8 h-0.5 bg-red-600" />
                <span className="text-red-500 text-xs font-bold uppercase tracking-widest">Structural</span>
              </div>
              <h1 className="font-heading text-4xl lg:text-6xl font-bold mb-6 text-white leading-tight">
                Structural<br /><span className="text-red-600">System Parts</span>
              </h1>
              <p className="font-body text-lg lg:text-xl text-gray-300 leading-relaxed">
                Complete selection of structural components including guide rails, hydraulic jacks, brackets, sling packages, and mounting hardware for elevator systems. All products ready for immediate order.
              </p>
            </div>
          </div>
        </section>

        {/* Filters Section */}
        <section className="bg-gray-900 py-6 border-b-2 border-red-600">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search by SKU or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-600 bg-gray-800 text-white font-body focus:outline-none focus:border-red-600 placeholder-gray-400"
                />
              </div>

              {/* Price Filter */}
              <div className="md:w-64">
                {!authLoading && user ? (
                <select
                  value={priceRange}
                  onChange={(e) => setPriceRange(e.target.value as typeof priceRange)}
                  className="w-full px-4 py-3 border-2 border-gray-600 bg-gray-800 text-white font-body focus:outline-none focus:border-red-600"
                >
                  <option value="all">All Prices</option>
                  <option value="under100">Under $100</option>
                  <option value="100-500">$100 - $500</option>
                  <option value="500-1000">$500 - $1,000</option>
                  <option value="over1000">Over $1,000</option>
                </select>
                ) : (
                  <div className="w-full px-4 py-3 border-2 border-gray-600 bg-gray-800 text-gray-400 font-body text-sm flex items-center gap-2">
                    <svg className="w-4 h-4 flex-shrink-0 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                    Login to filter by price
                  </div>
                )}
              </div>
            </div>

            <div className="mt-3 text-gray-400 font-body text-sm">
              {productsLoading ? (
                'Loading products...'
              ) : (
                <span><span className="text-red-500 font-bold">{filteredProducts.length}</span> of {products.length} products</span>
              )}
            </div>
          </div>
        </section>

        {/* Products Grid */}
        <section className="py-12 lg:py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            {productsLoading ? (
              <div className="text-center py-16">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
                <p className="mt-4 text-gray-500 font-body">Loading products...</p>
              </div>
            ) : error ? (
              <div className="text-center py-16">
                <p className="text-xl text-red-600 font-body mb-4">{error}</p>
                <button
                  onClick={loadProducts}
                  className="text-red-600 hover:text-red-700 font-semibold transition-colors"
                >
                  Try again
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProducts.map((product) => (
                  <AuthAwareProductCard
                    key={product.id}
                    product={product}
                    onClick={openProductModal}
                    isAuthenticated={!!user}
                    authLoading={authLoading}
                  />
                ))}
              </div>
            )}

            {/* No Results Message */}
            {!productsLoading && !error && filteredProducts.length === 0 && (
              <div className="text-center py-16">
                <p className="text-xl text-gray-500 font-body mb-4">
                  No products found matching your criteria
                </p>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setPriceRange('all');
                  }}
                  className="text-red-600 hover:text-red-700 font-semibold transition-colors"
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section — black with red accent */}
        <section className="relative bg-black py-16 lg:py-24 overflow-hidden">
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-red-600 opacity-10" style={{ clipPath: 'polygon(0 100%, 100% 100%, 0 0)' }} />
          <div className="absolute top-0 right-0 w-2 h-full bg-red-600" />
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center space-y-6 relative z-10">
            <div className="inline-flex items-center gap-2 justify-center">
              <div className="w-8 h-0.5 bg-red-600" />
              <span className="text-red-500 text-xs font-bold uppercase tracking-widest">Expert Support</span>
              <div className="w-8 h-0.5 bg-red-600" />
            </div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white">
              Need Help Selecting the Right Components?
            </h2>
            <p className="font-body text-lg text-gray-300 leading-relaxed">
              Our technical team can help you identify the exact mechanical components you need for your elevator system. Contact us for expert guidance on guide rails, jacks, and mounting solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/contact"
                className="inline-block px-8 py-4 bg-red-600 text-white font-bold text-sm tracking-wide border-2 border-red-600 transition-all duration-200 hover:bg-red-700 hover:border-red-700 hover:-translate-y-1 hover:shadow-lg"
              >
                Contact Technical Support
              </Link>
              <Link
                href="/submit-order"
                className="inline-block px-8 py-4 bg-transparent text-white font-bold text-sm tracking-wide border-2 border-white transition-all duration-200 hover:bg-white hover:text-black hover:-translate-y-1 hover:shadow-lg"
              >
                Submit Complete Order
              </Link>
            </div>
          </div>
        </section>
      </div>
      <Footer />

      {/* Product Detail Modal */}
      {isModalOpen && selectedProduct && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-70"
          onClick={closeProductModal}
        >
          <div
            className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto border-2 border-black shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-black border-b-2 border-red-600 px-6 py-4 flex justify-between items-center z-10">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-red-600" />
                <h2 className="font-heading text-xl font-bold text-white">
                  Product Details
                </h2>
              </div>
              <button
                onClick={closeProductModal}
                className="text-gray-400 hover:text-white transition-colors p-2"
                aria-label="Close modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 md:p-8">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Product Image */}
                <div className="bg-gray-50 border-2 border-black p-6 flex items-center justify-center min-h-[400px] relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-red-600" />
                  {selectedProduct.image_url ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <img
                        src={selectedProduct.image_url}
                        alt={selectedProduct.image_alt || selectedProduct.description || 'Product image'}
                        className="object-contain w-full h-full max-h-[500px]"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const placeholder = e.currentTarget.nextElementSibling;
                          if (placeholder) {
                            (placeholder as HTMLElement).style.display = 'flex';
                          }
                        }}
                      />
                      <div className="w-full h-full flex flex-col items-center justify-center" style={{ display: 'none' }}>
                        <svg className="w-24 h-24 mb-4 text-gray-400 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                        </svg>
                        <p className="text-gray-500 font-body text-center">Image failed to load</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center flex flex-col items-center justify-center">
                      <svg className="w-24 h-24 mb-4 text-gray-400 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                      </svg>
                      <p className="text-gray-500 font-body">No image available</p>
                    </div>
                  )}
                </div>

                {/* Product Information */}
                <div className="space-y-5">
                  {/* SKU */}
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-widest">
                      SKU / Part Number
                    </label>
                    <div className="bg-black px-4 py-2 inline-block">
                      <span className="font-mono text-lg font-bold text-white">
                        {selectedProduct.part_id}
                      </span>
                    </div>
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-widest">
                      Category
                    </label>
                    <p className="font-body text-gray-800 font-semibold">
                      {selectedProduct.category || 'Structural System Parts'}
                    </p>
                  </div>

                  {/* Price */}
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-widest">
                      Unit Price
                    </label>
                    {!authLoading && user ? (
                    <div className="flex items-baseline gap-2">
                      <span className="font-heading text-4xl font-bold text-black">
                        ${selectedProduct.price?.toFixed(2) || '0.00'}
                      </span>
                      <span className="text-gray-500 font-body text-sm">per unit</span>
                    </div>
                    ) : (
                    <Link
                      href="/login"
                      className="inline-flex items-center gap-2 px-4 py-3 bg-red-50 border-2 border-red-600 text-red-700 font-bold hover:bg-red-100 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                      Login to view pricing
                    </Link>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-widest">
                      Full Description
                    </label>
                    <div className="bg-gray-50 p-4 border-l-4 border-red-600 max-h-64 overflow-y-auto">
                      <p className="font-body text-gray-700 whitespace-pre-wrap leading-relaxed">
                        {selectedProduct.description || 'No description available'}
                      </p>
                    </div>
                  </div>

                  {/* Specifications */}
                  {selectedProduct.specifications && (
                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-widest">
                        Specifications
                      </label>
                      <div className="bg-gray-50 p-4 border-l-4 border-black">
                        <p className="font-body text-gray-700 whitespace-pre-wrap">
                          {selectedProduct.specifications}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-4 pt-4 border-t-2 border-black">
                    {!authLoading && user ? (
                    <Link
                      href="/submit-order"
                      className="flex-1 text-center px-6 py-3 bg-red-600 text-white font-bold text-sm tracking-wide border-2 border-red-600 transition-all duration-200 hover:bg-red-700 hover:-translate-y-0.5 hover:shadow-md"
                    >
                      Add to Order
                    </Link>
                    ) : (
                    <Link
                      href="/login"
                      className="flex-1 text-center px-6 py-3 bg-red-600 text-white font-bold text-sm tracking-wide border-2 border-red-600 transition-all duration-200 hover:bg-red-700 hover:-translate-y-0.5 hover:shadow-md"
                    >
                      Login to Order
                    </Link>
                    )}
                    <Link
                      href="/contact"
                      className="flex-1 text-center px-6 py-3 bg-white text-black font-bold text-sm tracking-wide border-2 border-black transition-all duration-200 hover:bg-black hover:text-white hover:-translate-y-0.5"
                    >
                      Contact Support
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MechanicalPartsPage;